import { IF, NO, nil, UIDialogView, UINativeScrollView, UITextView, UIView, YES } from "uicore-ts"
import { RHCore } from "../../Core/RHCore"
import { RHFocusManager } from "../../Custom components/RH/Keyboard navigation/RHFocusManager"
import { RHPageFocusManager } from "../../Custom components/RH/Keyboard navigation/RHPageFocusManager"
import { RHAlertBannerView } from "../../Custom components/RH/RHAlertBannerView"
import { RHButton } from "../../Custom components/RH/RHButton"
import { RHCheckbox } from "../../Custom components/RH/RHCheckbox"
import { RHFileUploadView } from "../../Custom components/RH/RHFileUploadView"
import { RHTextArea } from "../../Custom components/RH/RHTextArea"
import { RHButtons, RHDialogs, RHSpacing } from "../../Custom components/RH/Utils_slop/RHDesignTokens"
import type { RHOffererCompanyEligibleForPrivateMessage } from "../../SocketClientFunctions"


class RHOffererCompanyCandidateRow extends UIView {

    readonly checkbox = new RHCheckbox().addedAsSubviewToView(this)

    readonly statusLabel = new UITextView()
        .configuredWithObject({
            textAlignment: UITextView.textAlignment.right,
            textColor: RHDialogs.questionMetadataDateTextColor,
            style: {
                fontFamily: RHDialogs.questionMetadataDateFontFamily,
                fontSize: `${RHDialogs.questionMetadataDateFontSize}px`,
                fontWeight: RHDialogs.questionMetadataDateFontWeight
            }
        })
        .addedAsSubviewToView(this)

    candidate!: RHOffererCompanyEligibleForPrivateMessage

    configureWithCandidate(candidate: RHOffererCompanyEligibleForPrivateMessage) {
        this.candidate = candidate

        this.checkbox.configuredWithObject({
            titleLabel: { text: candidate.companyName },
            selected: NO
        })
        this.statusLabel.text = IF(candidate.hasExistingThread)(() =>
            "Vestlus juba alustatud"
        ).ELSE(() =>
            ""
        )
    }

    override layoutSubviews() {
        super.layoutSubviews()
        const bounds = this.contentBoundsWithInset(0)
        bounds.distributeViewsAlongWidth(
            [this.checkbox, this.statusLabel],
            undefined,
            RHSpacing.small,
            [nil, 160]
        )
    }

    override intrinsicContentHeight(): number {
        return RHButtons.compactHeight
    }

}


// Bulk messages stay in each company's private thread; recipients never share a conversation.
export class RHStartPrivateMessageThreadDialogView extends UIDialogView {

    readonly contentView = new UINativeScrollView()
        .configuredWithObject({
            backgroundColor: RHDialogs.questionDialogBackgroundColor,
            style: {
                borderRadius: RHDialogs.questionDialogBorderRadius,
                boxShadow: RHDialogs.questionDialogBoxShadow,
                width: "90vw"
            }
        })
        .addedAsSubviewToView(this.view)

    readonly titleLabel = new UITextView(undefined, RHDialogs.questionTitleTextViewType)
        .configuredWithObject({
            text: "Teabevahetus",
            textColor: RHDialogs.questionTitleTextColor
        })
        .addedAsSubviewToView(this.contentView)

    readonly candidatesHeaderLabel = new UITextView()
        .configuredWithObject({
            text: "<b>Pakkujad</b>",
            textAlignment: UITextView.textAlignment.left,
            style: {
                fontSize: RHDialogs.questionMetadataLabelFontSize,
                fontWeight: RHDialogs.questionMetadataLabelFontWeight,
                fontFamily: RHDialogs.questionMetadataLabelFontFamily,
                margin: "0",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                color: RHDialogs.questionMetadataLabelTextColor.stringValue
            }
        })
        .addedAsSubviewToView(this.contentView)

    readonly candidatesContainerView = new UIView().addedAsSubviewToView(this.contentView)

    readonly messageLabel = new UITextView(undefined, RHDialogs.questionAnswerSectionTextViewType)
        .configuredWithObject({
            text: "Sõnum",
            textColor: RHDialogs.questionAnswerSectionTextColor,
            style: {
                fontWeight: "600",
                margin: "0"
            }
        })
        .addedAsSubviewToView(this.contentView)

    readonly messageTextArea = new RHTextArea()
        .configuredWithObject({
            placeholderText: "Sisesta sõnum siia"
        })
        .addedAsSubviewToView(this.contentView)

    readonly messageFileUploadView = new RHFileUploadView()
        .configuredWithObject({
            variant: RHFileUploadView.variant.full,
            bulkDownloadEnabled: NO
        })
        .addedAsSubviewToView(this.contentView)

    readonly errorBanner = new RHAlertBannerView()
        .configuredWithObject({ alertType: RHAlertBannerView.type.Error })

    readonly cancelButton = new RHButton()
        .configuredWithObject({
            titleLabel: {
                text: "Sulge",
                style: { fontSize: RHDialogs.questionFooterButtonFontSize }
            },
            variant: RHDialogs.questionCancelButtonVariant,
            contentPadding: RHDialogs.questionFooterButtonContentPadding,
            controlEventTargetAccumulator: {
                PrimaryActionTriggered: () => {
                    this.dismiss()
                }
            }
        })
        .addedAsSubviewToView(this.contentView)

    readonly submitButton = new RHButton()
        .configuredWithObject({
            titleLabel: {
                text: "Saada sõnum",
                style: { fontSize: RHDialogs.questionFooterButtonFontSize }
            },
            variant: RHDialogs.questionSubmitButtonVariant,
            contentPadding: RHDialogs.questionFooterButtonContentPadding,
            controlEventTargetAccumulator: {
                PrimaryActionTriggered: () => {
                    void this._submit()
                }
            }
        })
        .addedAsSubviewToView(this.contentView)

    override _focusManager = new RHFocusManager(() => {
        const items: UIView[] = [this.messageTextArea]
        for (const row of this._candidateRows) {
            items.push(row.checkbox)
        }
        items.push(this.cancelButton, this.submitButton)
        return items
    })

    _procurementId = ""
    _candidateRows: RHOffererCompanyCandidateRow[] = []
    _didStartThreads?: (offererCompanyIds: string[]) => void
    _isSubmitting = NO
    _bottomView = new UIView().addedAsSubviewToView(this.contentView)

    constructor(elementID?: string) {
        super(elementID)
        this.view = this.contentView
        this.dismissesOnTapOutside = YES
    }


    override showInRootView(animated: boolean) {
        super.showInRootView(animated)
        RHPageFocusManager.shared.pushManagerAsNewLayer(this._focusManager)
    }

    override dismiss(animated?: boolean) {
        RHPageFocusManager.shared.popLayer()
        super.dismiss(animated)
    }


    async showForProcurement(procurementId: string, didStartThreads: (offererCompanyIds: string[]) => void) {
        this._procurementId = procurementId
        this._didStartThreads = didStartThreads
        this.messageTextArea.text = ""
        this.messageFileUploadView.clearFiles()
        this.errorBanner.removeFromSuperview()

        this.showInRootView(YES)
        this.setNeedsLayout()

        try {
            const candidates = await RHCore.sharedInstance.privateMessageStore
                .retrieveEligibleOffererCompanies(procurementId)
            this._loadCandidateRows(candidates)
        }
        catch (error) {
            console.error("RHStartPrivateMessageThreadDialogView: failed to load candidates:", error)
            this._showError("Pakkujate laadimine ebaõnnestus.")
        }
    }


    _loadCandidateRows(candidates: RHOffererCompanyEligibleForPrivateMessage[]) {
        this._candidateRows.everyElement.removeFromSuperview()
        this._candidateRows = candidates.map(candidate => {
            const row = new RHOffererCompanyCandidateRow().addedAsSubviewToView(this.candidatesContainerView)
            row.configureWithCandidate(candidate)
            return row
        })
        this.setNeedsLayoutUpToRootView()
    }


    _selectedCompanyIds(): string[] {
        return this._candidateRows
            .filter(row => row.checkbox.selected)
            .map(row => row.candidate.companyId)
    }


    _showError(text: string) {
        this.errorBanner.configureBannerWithObject({ text })
        this.errorBanner.addedAsSubviewToView(this.contentView)
        this.setNeedsLayoutUpToRootView()
    }


    async _submit() {
        const selectedCompanyIds = this._selectedCompanyIds()
        if (selectedCompanyIds.length === 0) {
            this._showError("Palun vali vähemalt üks pakkuja.")
            return
        }
        if (this.messageTextArea.text.trim().length === 0) {
            this._showError("Palun sisesta sõnum.")
            return
        }

        this.errorBanner.removeFromSuperview()
        this._isSubmitting = YES
        this.submitButton.enabled = NO
        this.submitButton.titleLabel.text = "Saatmine..."
        this.setNeedsLayoutUpToRootView()

        try {
            const results = await RHCore.sharedInstance.privateMessageStore.startThreadsWithOffererCompanies({
                procurementId: this._procurementId,
                offererCompanyIds: selectedCompanyIds,
                bodyText: this.messageTextArea.text,
                fileIds: this.messageFileUploadView.uploadedFiles.map(uploadedFile => uploadedFile.id)
            })
            const succeededCompanyIds = results.filter(result => !result.error).map(result => result.companyId)
            const failedResults = results.filter(result => result.error)

            if (succeededCompanyIds.length > 0) {
                this._didStartThreads?.(succeededCompanyIds)
            }
            if (failedResults.length === 0) {
                this.dismiss()
            }
            else {
                const failedNames = failedResults.map(result =>
                    this._candidateRows.find(row => row.candidate.companyId === result.companyId)
                        ?.candidate.companyName ?? result.companyId
                )
                this._showError(`Ei õnnestunud saata: ${failedNames.join(", ")}.`)
            }
        }
        catch (error) {
            console.error("RHStartPrivateMessageThreadDialogView: failed to start threads:", error)
            let errorText = "Sõnumi saatmine ebaõnnestus."
            if (error instanceof Error) {
                errorText = error.message
            }
            this._showError(errorText)
        }
        finally {
            this._isSubmitting = NO
            this.submitButton.enabled = YES
            this.submitButton.titleLabel.text = "Saada sõnum"
        }
    }


    _footerButtonWidth(button: RHButton): number {
        return Math.min(
            RHDialogs.questionFooterButtonMaxWidth,
            Math.max(
                RHDialogs.questionFooterButtonMinWidth,
                button.titleLabel.intrinsicContentWidth(RHDialogs.questionFooterButtonHeight) +
                RHDialogs.questionFooterButtonContentPadding * 2 +
                RHDialogs.questionFooterButtonTextSafetyBuffer
            )
        )
    }


    override layoutSubviews() {
        super.layoutSubviews()

        const padding = this.core.paddingLength
        const superBounds = this.superview?.bounds ?? this.bounds
        const dialogWidth = Math.min(
            RHDialogs.questionDialogMaxWidth,
            superBounds.width * RHDialogs.questionDialogWidthViewportRatio
        )

        this.contentView.frame = superBounds
            .rectangleWithWidth(dialogWidth, 0.5)
            .rectangleWithHeight(0)

        const bounds = this.contentView.contentBoundsWithInset(padding * 1.5)

        let current = bounds
            .rectangleWithHeight(this.titleLabel)
            .assignedAsFrameOfView(this.titleLabel)
            .rectangleForNextRow(
                RHDialogs.questionTitleBottomGap,
                RHDialogs.questionMetadataRowHeight
            )
            .assignedAsFrameOfView(this.candidatesHeaderLabel)

        let candidatesHeight: number = RHButtons.compactHeight
        if (this._candidateRows.length > 0) {
            candidatesHeight = this._candidateRows.length * RHButtons.compactHeight +
                (this._candidateRows.length - 1) * RHSpacing.small
        }

        current = current
            .rectangleForNextRow(RHDialogs.questionQuestionBubbleTopGap, candidatesHeight)
            .assignedAsFrameOfView(this.candidatesContainerView)

        this.candidatesContainerView.contentBoundsWithInset(0)
            .framesByDistributingViewsAsColumn(this._candidateRows, RHSpacing.small)

        current = current
            .IF(this.errorBanner.superview === this.contentView)
                .rectangleForNextRow(RHDialogs.questionQuestionBubbleTopGap, this.errorBanner)
                .assignedAsFrameOfView(this.errorBanner)
            .ENDIF()

        const lastContentFrame = current
            .rectangleForNextRow(RHDialogs.questionAnswerSectionTopGap, this.messageLabel)
            .assignedAsFrameOfView(this.messageLabel)
            .rectangleForNextRow(
                RHDialogs.questionAnswerTextAreaTopGap,
                RHDialogs.questionAnswerTextAreaHeight
            )
            .assignedAsFrameOfView(this.messageTextArea)
            .rectangleForNextRow(
                RHDialogs.questionAnswerTextAreaTopGap,
                this.messageFileUploadView
            )
            .assignedAsFrameOfView(this.messageFileUploadView)

        let cancelButtonWidth = this._footerButtonWidth(this.cancelButton)
        let submitButtonWidth = this._footerButtonWidth(this.submitButton)
        let footerButtonsRowWidth =
            cancelButtonWidth +
            submitButtonWidth +
            RHDialogs.questionFooterButtonGap

        if (footerButtonsRowWidth > bounds.width) {
            const compactButtonWidth = Math.max(
                0,
                (bounds.width - RHDialogs.questionFooterButtonGap) * 0.5
            )
            cancelButtonWidth = compactButtonWidth
            submitButtonWidth = compactButtonWidth
            footerButtonsRowWidth = bounds.width
        }

        lastContentFrame
            .rectangleForNextRow(
                RHDialogs.questionFooterButtonsTopGap,
                RHDialogs.questionFooterButtonHeight
            )
            .rectangleWithWidth(footerButtonsRowWidth, 0.5)
            .distributeViewsAlongWidth(
                [this.cancelButton, this.submitButton],
                undefined,
                RHDialogs.questionFooterButtonGap,
                [cancelButtonWidth, submitButtonWidth]
            )
            .rectangleForNextRow(0, RHDialogs.questionFooterBottomPadding)
            .assignedAsFrameOfView(this._bottomView)

        let availableHeight = superBounds.height
        if (this._fillsViewport) {
            availableHeight = window.innerHeight / UIView.pageScale
        }
        const finalHeight = Math.min(
            this._bottomView.frame.max.y,
            availableHeight - padding * 3
        )

        this.contentView.frame = superBounds
            .rectangleWithHeight(availableHeight)
            .rectangleWithWidth(dialogWidth, 0.5)
            .rectangleWithHeight(finalHeight, 0.5)
    }

}
